import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import type { TImportTargets } from "types";

import type { TFetchStatus } from "./types";

const API = import.meta.env.REACT_APP_API_ENDPOINT;

// Active jobs only (QUEUED=0, PROCESSING=1, IDLE=5) — used for polling (badge, management, EDI)
export const fetchActiveJobs = createAsyncThunk("jobs/fetchActiveJobs", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(API + "/company/jobs", {
            params: { status: "0,1,5" },
        });
        return response.data.data as Job[];
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

// Last N completed jobs for a given importType — one-shot fetch, not polled
export const fetchLastCompletedJob = createAsyncThunk(
    "jobs/fetchLastCompletedJob",
    async ({ importType }: { importType: string }, { rejectWithValue }) => {
        try {
            const response = await axios.get(API + "/company/jobs", {
                params: { status: "2", importType, limit: 10, createdAfterDays: 7 },
            });
            // limit triggers paginated response on backend — unwrap jobs array
            const paginated = response.data.data as { data: Job[] };
            return paginated.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Paginated all-jobs list — used for admin ImportTracking panel
let latestPaginatedJobsRequest = 0;

export const fetchJobsPaginated = createAsyncThunk(
    "jobs/fetchJobsPaginated",
    async ({ page = 1, limit = 15 }: { page?: number; limit?: number; silent?: boolean } = {}, { rejectWithValue }) => {
        const requestSeq = ++latestPaginatedJobsRequest;
        try {
            const response = await axios.get(API + "/company/jobs", { params: { page, limit } });
            if (requestSeq !== latestPaginatedJobsRequest) {
                return rejectWithValue({ stale: true });
            }
            return response.data.data as { data: Job[]; total: number; page: number; limit: number };
        } catch (error: any) {
            if (requestSeq !== latestPaginatedJobsRequest) {
                return rejectWithValue({ stale: true });
            }
            return rejectWithValue(error.response.data);
        }
    },
);

// Kept for backward compatibility (SubscriptionManagementModal, AnalyticsPage)
export const fetchProcessingJobs = createAsyncThunk("jobs/fetchProcessingJobs", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(API + "/company/jobs", {
            params: { status: String(ProcessingJobStatus.PROCESSING) },
        });
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

// List analytics jobs
export const fetchAnalyticsJobs = createAsyncThunk("jobs/analytics/fetchJobs", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(API + "/company/jobs/analytics");
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const forceUpdateJobStatus = createAsyncThunk(
    "jobs/forceUpdateJobStatus",
    async ({ jobId, status }: { jobId: number; status: ProcessingJobStatus }, { rejectWithValue }) => {
        try {
            const response = await axios.put(`${API}/company/jobs/${jobId}`, { status });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchJob = createAsyncThunk("jobs/fetchJob", async ({ jobId }: { jobId: number }, { rejectWithValue }) => {
    try {
        const response = await axios.get(API + `/company/jobs/${jobId}`);
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const rollbackFile = createAsyncThunk(
    "jobs/rollbackFile",
    async ({ jobId }: { jobId: number }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(axios.put(API + `/company/jobs/${jobId}/rollback`), {
                pending: t("History.Rollback.RollbackPending"),
                success: t("History.Rollback.RollbackSuccessful"),
                error: t("History.Rollback.RollbackFailed"),
            });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export type TAnalyticsImportTargets = "compute-analytics" | "salesByChannels";
export type ImportJobStep =
    | "started"
    | "queued"
    | "raw-file-uploading-started"
    | "raw-file-uploading-failed"
    | "raw-file-uploading-success"
    | "raw-file-parsing-ready"
    | "raw-file-parsing-started"
    | "raw-file-parsing-failed"
    | "raw-file-parsing-success"
    | "corrections-required"
    | "sales-importing-ready"
    | "sales-importing-started"
    | "sales-importing-failed"
    | "sales-importing-success"
    | "sales-importing-archived"
    | "reverting";

export interface Job {
    reason: string | null;
    id: number;
    createdAt: Date;
    companyId: number;
    status: ProcessingJobStatus;
    progress: string;
    createdBy: number | null;
    importType: TImportTargets | TAnalyticsImportTargets;
    modifiedAt: Date | null;
    metadata?: TJobMetadata;
}

export enum ProcessingJobStatus {
    QUEUED = 0,
    PROCESSING = 1,
    DONE = 2,
    FAILED = 3,
    REVERTED = 4,
    IDLE = 5,
}

export type TJobMetadata = {
    step?: ImportJobStep;
    taskToken?: string;
    periodId?: number;
    errorCode?: string;
    errorMessage?: string;
    errorParams?: Record<string, string>;
    errorDetails?: string;
};

export interface JobsInitialState {
    // Active jobs (QUEUED/PROCESSING/IDLE) + any individual jobs fetched by fetchJob/fetchLastCompletedJob
    jobs: Job[];
    job: Job;
    jobsStatus: TFetchStatus;

    processingJobs: Job[];
    processingJobsStatus?: TFetchStatus;

    analyticsJobs: { computing: boolean; jobs: Job[] };
    analyticsJobsStatus: TFetchStatus;

    // Admin paginated view (ImportTracking)
    allJobs: Job[];
    allJobsPagination: { total: number; page: number; limit: number };
    allJobsStatus: TFetchStatus;
}

const ACTIVE_STATUSES = new Set([ProcessingJobStatus.QUEUED, ProcessingJobStatus.PROCESSING, ProcessingJobStatus.IDLE]);

const initialState: JobsInitialState = {
    jobs: [],
    job: {} as Job,
    jobsStatus: "idle",

    processingJobs: [],
    processingJobsStatus: "idle",

    analyticsJobs: { computing: false, jobs: [] },
    analyticsJobsStatus: "idle",

    allJobs: [],
    allJobsPagination: { total: 0, page: 1, limit: 15 },
    allJobsStatus: "idle",
};

export const jobsSlice = createSlice({
    name: "jobs",
    initialState,
    reducers: {
        setJobStatus(state, { payload }: PayloadAction<{ id: number; status: ProcessingJobStatus }>) {
            state.jobs = state.jobs.map((job) => (job.id === payload.id ? { ...job, status: payload.status } : job));
            if (state.job?.id === payload.id) {
                state.job = { ...state.job, status: payload.status };
            }
        },
    },
    extraReducers: (builder) => {
        // Active jobs poll — replaces active statuses, preserves DONE/FAILED/REVERTED already in store
        builder.addCase(fetchActiveJobs.fulfilled, (state, { payload }) => {
            state.jobs = [...state.jobs.filter((j) => !ACTIVE_STATUSES.has(j.status)), ...(payload ?? [])];
            state.jobsStatus = "fulfilled";
        });
        builder.addCase(fetchActiveJobs.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.jobsStatus = "rejected";
        });

        // Last completed job — merge into jobs so useLastCompletedJob can find them
        builder.addCase(fetchLastCompletedJob.fulfilled, (state, { payload }) => {
            const ids = new Set(payload.map((j) => j.id));
            state.jobs = [...state.jobs.filter((j) => !ids.has(j.id)), ...payload];
        });

        // Paginated admin view
        builder.addCase(fetchJobsPaginated.fulfilled, (state, { payload }) => {
            state.allJobs = payload.data;
            state.allJobsPagination = { total: payload.total, page: payload.page, limit: payload.limit };
            state.allJobsStatus = "fulfilled";
        });
        builder.addCase(fetchJobsPaginated.pending, (state, { meta }) => {
            if (meta.arg?.silent) return;
            state.allJobsStatus = "pending";
        });
        builder.addCase(fetchJobsPaginated.rejected, (state, { payload }) => {
            if ((payload as { stale?: boolean } | undefined)?.stale) return;
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.allJobsStatus = "rejected";
        });

        builder.addCase(fetchProcessingJobs.pending, (state) => {
            state.processingJobsStatus = "pending";
        });
        builder.addCase(fetchProcessingJobs.fulfilled, (state, { payload }) => {
            state.processingJobs = payload;
            state.processingJobsStatus = "fulfilled";
        });
        builder.addCase(fetchProcessingJobs.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.processingJobsStatus = "rejected";
        });

        builder.addCase(fetchAnalyticsJobs.fulfilled, (state, { payload }) => {
            const computing = payload.some((job: Job) => job.status === ProcessingJobStatus.PROCESSING);
            state.analyticsJobs = { computing, jobs: payload };
            state.analyticsJobsStatus = "fulfilled";
        });
        builder.addCase(fetchAnalyticsJobs.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsJobsStatus = "rejected";
        });

        // Individual job fetch — always merge into jobs store (supports live status in History page)
        builder.addCase(fetchJob.fulfilled, (state, { payload }) => {
            state.job = payload;
            state.jobs = state.jobs.filter((job) => job.id !== payload.id).concat([payload]);
        });
        builder.addCase(fetchJob.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(forceUpdateJobStatus.fulfilled, (state, { payload }) => {
            state.jobs = state.jobs.map((job) => (job.id === payload.id ? payload : job));
            state.allJobs = state.allJobs.map((job) => (job.id === payload.id ? payload : job));
        });
        builder.addCase(forceUpdateJobStatus.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(rollbackFile.fulfilled, (state, { payload }) => {
            state.jobs = state.jobs.map((job) =>
                job.id === payload.id ? { ...job, status: ProcessingJobStatus.REVERTED } : job,
            );
        });
    },
});

export const { setJobStatus } = jobsSlice.actions;
export default jobsSlice.reducer;
